<template>
  <div>
    <h6 class="heading-small text-muted mb-4 text-truncate">
      <span v-if="cardOrderData === null">Create Card Order</span>
      <span v-else>View Card Order</span>
    </h6>

    <form id="form-proper">
      <div class="py-2" style="border-bottom: 1px solid #e0e0e0">
        <div class="row">
          <div class="col">
            <base-input label="Membership Program" required>
              <a-select
                v-model="formData.membership_program_id"
                :disabled="viewMode || cardOrderData !== null"
                @change="onChangeMembershipProgram"
              >
                <a-select-option
                  v-for="membership_program in membershipPrograms"
                  :key="membership_program.id"
                  :value="membership_program.id"
                >
                  {{ membership_program.name }}
                </a-select-option>
              </a-select>
              <div
                v-if="$v.formData.membership_program_id.$dirty"
                class="text-danger"
              >
                <div
                  class="error"
                  v-if="!$v.formData.membership_program_id.required"
                >
                  Field is required
                </div>
              </div>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Membership Level" required>
              <a-select
                v-model="formData.membership_level_id"
                :disabled="viewMode || cardOrderData !== null"
              >
                <a-select-option
                  v-for="membershipProgram in membershipLevels"
                  :key="membershipProgram.id"
                  :value="membershipProgram.id"
                >
                  {{ membershipProgram.name }}
                </a-select-option>
              </a-select>
              <div
                v-if="$v.formData.membership_level_id.$dirty"
                class="text-danger"
              >
                <div
                  class="error"
                  v-if="!$v.formData.membership_level_id.required"
                >
                  Field is required
                </div>
              </div>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Card Type" required>
              <a-select
                v-model="formData.card_type_id"
                :disabled="viewMode || cardOrderData !== null"
              >
                <a-select-option
                  v-for="card_type in cardTypes"
                  :key="card_type.id"
                  :value="card_type.id"
                >
                  {{ card_type.name }}
                </a-select-option>
              </a-select>
              <div v-if="$v.formData.card_type_id.$dirty" class="text-danger">
                <div class="error" v-if="!$v.formData.card_type_id.required">
                  Field is required
                </div>
              </div>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Site">
              <a-select
                v-model="formData.site_id"
                :disabled="viewMode || cardOrderData !== null"
                show-search
                optionFilterProp="children"
              >
                <a-select-option
                  v-for="site in sites"
                  :key="site.id"
                  :value="site.id"
                >
                  {{ site.name }}
                </a-select-option>
              </a-select>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Card Supplier" required>
              <a-select
                v-model="formData.card_supplier_id"
                :disabled="viewMode || cardOrderData !== null"
              >
                <a-select-option
                  v-for="supp in cardSuppliers"
                  :key="supp.id"
                  :value="supp.id"
                >
                  {{ supp.name }}
                </a-select-option>
              </a-select>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Quantity" required>
              <a-input
                v-model.trim="$v.formData.quantity.$model"
                :disabled="viewMode"
              />
              <div v-if="$v.formData.quantity.$dirty" class="text-danger">
                <div class="error" v-if="!$v.formData.quantity.required">
                  Field is required
                </div>
              </div>
            </base-input>
          </div>
        </div>
      </div>
      <div
        style="
          position: absolute;
          bottom: 0px;
          right: 0px;
          width: -webkit-fill-available;
          background-color: #f2f2f2;
          padding: 10px;
        "
      >
        <div class="container mx-0 px-0">
          <div class="row">
            <div class="col-auto mr-auto">
              <b-button
                size="sm"
                class="float-md-left"
                style="margin-left: 8px"
                @click.prevent="closeDrawer"
              >
                Cancel
              </b-button>
            </div>
            <div class="col-auto">
              <template>
                <a-spin :spinning="formSubmitting">
                  <div v-if="cardOrderData === null">
                    <b-button
                      size="sm"
                      variant="primary"
                      @click.prevent="createCardOrder"
                    >
                      Create
                    </b-button>
                  </div>

                  <div v-else>
                    <b-button
                      v-if="selectedActions.includes('Approve')"
                      size="sm"
                      variant="success"
                      :disabled="
                        cardOrderData === null ||
                        cardOrderData.cardOrderStatus.status_code !==
                          'ORDER_APPROVAL'
                      "
                      @click.prevent="approveCardOrder"
                    >
                      Approve
                    </b-button>
                    <b-button
                      v-if="selectedActions.includes('Reject')"
                      size="sm"
                      variant="danger"
                      :disabled="
                        cardOrderData === null ||
                        cardOrderData.cardOrderStatus.status_code !==
                          'ORDER_APPROVAL'
                      "
                      @click.prevent="rejectCardOrder"
                    >
                      Reject
                    </b-button>
                  </div>
                </a-spin>
              </template>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import HTTPRequest from '../../controllers/httpRequest';
import { required } from 'vuelidate/lib/validators';
import secureStorage from '../../controllers/secureStorage';

export default {
  name: 'CardOrderForm',
  props: {
    cardOrderData: Object,
    viewMode: Boolean,
    selectedActions: Array
  },
  data() {
    return {
      formSubmitting: false,
      currentSiteId: secureStorage.getItem('currentSiteId'),
      formData: {
        membership_program_id: null,
        membership_level_id: null,
        site_id: null,
        card_type_id: null,
        card_supplier_id: null,
        quantity: null
      },
      membershipPrograms: [],
      cardTypes: [],
      membershipLevels: [],
      sites: [],
      cardSuppliers: []
    };
  },
  validations: {
    formData: {
      membership_program_id: {
        required
      },
      membership_level_id: { required },
      card_type_id: { required },
      quantity: { required }
    }
  },
  methods: {
    async getMembershipPrograms() {
      const response = await HTTPRequest.getRequest(
        '/api/v1/clp_membership_programs'
      );
      this.membershipPrograms = response.data.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },

    async getCardTypes(membershipProgramId) {
      const data = await HTTPRequest.getRequest(
        `/api/v1/clp_membership_programs/${membershipProgramId}/card_types`
      );
      this.cardTypes = data.sort((a, b) => a.name.localeCompare(b.name));
    },

    async getMembershipLevels(membershipProgramId) {
      const data = await HTTPRequest.getRequest(
        `/api/v1/clp_membership_programs/${membershipProgramId}/membership_levels`
      );
      this.membershipLevels = data.sort((a, b) => a.name.localeCompare(b.name));
    },

    async getSites() {
      const sites = secureStorage.getItem('visitingSites');
      this.sites = sites.sort((a, b) => a.name.localeCompare(b.name));
      if (this.sites && this.currentSiteId) {
        let _site = this.sites.find(
          (s) => s.third_party_site_id == this.currentSiteId
        );
        this.formData.site_id = _site.id;
      }
    },

    async getCardSuppliers() {
      const response = await HTTPRequest.getRequest(
        `/api/v1/card_suppliers?status_code=A`
      );
      this.cardSuppliers = response.data.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },

    async createCardOrder() {
      this.$v.formData.$touch();

      if (!this.$v.formData.$invalid) {
        this.formSubmitting = true;
        const response = await HTTPRequest.postRequest(
          '/api/v1/clp_card_orders?origin_site_id=' + this.currentSiteId,
          {
            card_order: this.formData
          }
        );

        if (response.id) {
          this.closeDrawer();
          this.$root.$emit('refreshTable');
          this.$notification.success({
            message: 'Success',
            description: 'Data has been created.'
          });
        } else {
          this.$notification.error({
            message: 'Error',
            description: 'Something went wrong. Please try again.',
            placement: 'topLeft'
          });
        }
        this.formSubmitting = false;
      }
    },

    async approveCardOrder() {
      this.formSubmitting = true;
      const response = await HTTPRequest.putRequest(
        '/api/v1/clp_card_orders/' +
          this.cardOrderData.batch_number +
          '/approve?origin_site_id=' +
          this.currentSiteId,
        {
          card_order: {
            membership_program_id: this.cardOrderData.membership_program_id
          }
        }
      );

      if (response.id) {
        this.closeDrawer();
        this.$root.$emit('refreshTable');
        this.$notification.success({
          message: 'Success',
          description: 'Card order has been approved.'
        });
      } else {
        this.$notification.error({
          message: 'Error',
          description: 'Something went wrong. Please try again.',
          placement: 'topLeft'
        });
      }
      this.formSubmitting = false;
    },

    async rejectCardOrder() {
      this.formSubmitting = true;
      const response = await HTTPRequest.putRequest(
        '/api/v1/clp_card_orders/' +
          this.cardOrderData.batch_number +
          '/reject?origin_site_id=' +
          this.currentSiteId,
        {
          card_order: {
            membership_program_id: this.cardOrderData.membership_program_id
          }
        }
      );

      if (response.id) {
        this.closeDrawer();
        this.$root.$emit('refreshTable');
        this.$notification.success({
          message: 'Success',
          description: 'Card order has been rejected.'
        });
      } else {
        this.$notification.error({
          message: 'Error',
          description: 'Something went wrong. Please try again.',
          placement: 'topLeft'
        });
      }
      this.formSubmitting = false;
    },

    onChangeMembershipProgram() {
      this.formData.card_type_id = null;
      this.getCardTypes(this.formData.membership_program_id);
      this.getMembershipLevels(this.formData.membership_program_id);
    },

    closeDrawer() {
      this.clearForm();
      this.$emit('closeDrawer', null);
      this.$v.formData.$reset();
    },

    clearForm() {
      this.formData = {
        membership_program_id: null,
        membership_level_id: null,
        site_id: null,
        card_type_id: null,
        quantity: null,
        card_supplier_id: null
      };
    },

    setFormData(data = null) {
      if (data !== null) {
        const {
          membership_program_id,
          membership_level_id,
          site_id,
          card_type_id,
          quantity,
          card_supplier_id
        } = data;

        this.formData = {
          membership_program_id,
          membership_level_id,
          site_id,
          card_type_id,
          quantity,
          card_supplier_id
        };

        this.getCardTypes(membership_program_id);
        this.getMembershipLevels(membership_program_id);
      } else {
        this.clearForm();
      }
    }
  },
  created() {
    this.$store.dispatch('setCurrentModule', 'Card Order');
    this.getMembershipPrograms();
    this.getCardSuppliers();
    this.setFormData(this.cardOrderData);
    this.getSites();
  },
  watch: {
    cardOrderData: function (data) {
      this.setFormData(data);
    }
  }
};
</script>

<style scoped></style>
